/**
 * Sitzung und Erscheinungsbild.
 *
 * Der angemeldete Nutzer ist Server-State und liegt deshalb in TanStack Query,
 * nicht in einem eigenen Store: Er kann sich serverseitig ändern (Rolle
 * entzogen, Konto gesperrt), und dann muss die Oberfläche das mitbekommen,
 * statt einen eingefrorenen Stand weiterzuzeigen.
 *
 * Das Thema ist reiner UI-State und liegt in Zustand.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { api, ApiError, setCsrfToken, type Me } from './api'
import { applyScheme, type ThemeMode } from '../styles/scheme'

export const ME_KEY = ['me'] as const

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api.get<Me>('/api/me')
        setCsrfToken(me.csrf_token)
        return me
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setCsrfToken(null)
          return null
        }
        throw error
      }
    },
    // Nicht endlos wiederholen: Wer abgemeldet ist, bleibt abgemeldet, und
    // fünf Fehlversuche machen den Ladebalken nur länger.
    retry: false,
    staleTime: 30_000,
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: () => {
      setCsrfToken(null)
      queryClient.setQueryData(ME_KEY, null)
      queryClient.clear()
    },
  })
}

export function useSwitchTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tenantId: string) => api.post<Me>('/api/tenants/switch', { tenant_id: tenantId }),
    onSuccess: (me) => {
      setCsrfToken(me.csrf_token)
      queryClient.setQueryData(ME_KEY, me)
      // Alles andere hing am alten Betrieb und ist jetzt falsch.
      queryClient.invalidateQueries()
    },
  })
}

// ------------------------------------------------------------------ Thema ---

type ThemeState = {
  mode: ThemeMode | 'system'
  setMode: (mode: ThemeMode | 'system') => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => set({ mode }),
    }),
    {
      name: 'dienstplan-theme-store',
      // Der Schlüssel, den auch das Boot-Skript in index.html liest.
      partialize: (state) => ({ mode: state.mode }),
    },
  ),
)

/**
 * Welches Thema **tatsächlich** angezeigt wird.
 *
 * Bei „System" hängt das an der Einstellung des Geräts. Wer den Umschalter
 * gegen den gespeicherten Wert statt gegen diesen baut, bietet bei
 * Systemeinstellung „dunkel" noch einmal „dunkel" an — ein Klick, der sichtbar
 * nichts tut.
 */
export function resolvedTheme(mode: ThemeMode | 'system'): ThemeMode {
  return resolveMode(mode)
}

function resolveMode(mode: ThemeMode | 'system'): ThemeMode {
  if (mode !== 'system') return mode
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Hält Thema und Betriebsfarbe am Dokument.
 *
 * Läuft an einer Stelle, damit ein Wechsel des Betriebs, ein Wechsel des
 * Themas und ein Wechsel der Systemeinstellung nicht drei verschiedene Wege
 * durch dieselbe Logik nehmen.
 */
export function useAppearance(primaryColor: string | undefined): void {
  const mode = useThemeStore((state) => state.mode)

  useEffect(() => {
    const apply = () => {
      const resolved = resolveMode(mode)
      applyScheme(primaryColor, resolved)
      // Das Boot-Skript liest diesen Schlüssel beim nächsten Start.
      localStorage.setItem('dienstplan-theme', mode === 'system' ? '' : mode)
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', resolved === 'dark' ? '#121318' : '#4a5b92')
    }
    apply()

    if (mode !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [mode, primaryColor])
}
